import { useEffect, useState, ChangeEvent } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabase";
import { cn } from "../utils/cn";
import { CameraIcon } from "@heroicons/react/24/outline";

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    location: "",
    phone: "",
    role: "",
    photoURL: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPhotoFile, setNewPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;
        if (data) {
          setProfile({
            name: data.name || "",
            email: data.email || user.email || "",
            location:
              typeof data.address === "object" && data.address?.address
                ? data.address.address
                : data.address || "",
            phone: data.phone || "",
            role: data.role || "",
            photoURL:
              data.profile_image_url ||
              user.user_metadata?.avatar_url ||
              user.user_metadata?.picture ||
              "",
          });
        } else {
          setError("Profile not found.");
        }
      } catch (e: any) {
        setError("Failed to load profile.");
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setProfile((p) => ({ ...p, [field]: value }));
  };

  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewPhotoFile(e.target.files[0]);
      setProfile((p) => ({
        ...p,
        photoURL: URL.createObjectURL(e.target.files![0]),
      }));
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaveLoading(true);
    setError(null);
    setSuccess(null);
    try {
      let photoURLToSave = profile.photoURL;
      if (newPhotoFile) {
        const fileExt = newPhotoFile.name.split(".").pop();
        const fileName = `${user.id}_${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("profile-images")
          .upload(fileName, newPhotoFile);

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("profile-images").getPublicUrl(fileName);

        photoURLToSave = publicUrl;
      }

      const { error } = await supabase
        .from("users")
        .update({
          name: profile.name,
          address: { address: profile.location },
          phone: profile.phone,
          profile_image_url: photoURLToSave,
        })
        .eq("id", user.id);

      if (error) throw error;

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: profile.name,
          avatar_url: photoURLToSave,
        },
      });

      if (authError) throw authError;
      setProfile((p) => ({ ...p, photoURL: photoURLToSave }));
      setNewPhotoFile(null);
      setSuccess("Profile updated!");
      setEditMode(false);
    } catch (e: any) {
      setError("Failed to update profile.");
    }
    setSaveLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 border-4 rounded-full animate-spin border-emerald-500 border-t-transparent"></div>
          <p className="text-xl text-slate-600 font-bold">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      <div className="max-w-3xl p-12 mx-auto bg-white/95 backdrop-blur-2xl rounded-3xl shadow-3xl border border-white/30">
        <div className="flex flex-col items-center mb-12">
          <label
            htmlFor="photo-upload"
            className={cn(
              "flex items-center justify-center w-32 h-32 mb-6 overflow-hidden rounded-3xl cursor-pointer bg-gradient-to-br from-emerald-100 to-teal-100 group relative transition-all duration-300 transform hover:scale-105 shadow-2xl",
              editMode ? "hover:ring-4 hover:ring-emerald-400" : ""
            )}
            title={editMode ? "Click to change profile picture" : ""}
          >
            {profile.photoURL ? (
              <img
                src={profile.photoURL}
                alt={profile.name}
                className="object-cover w-32 h-32 rounded-3xl"
                draggable={false}
              />
            ) : (
              <span className="text-5xl font-bold select-none text-emerald-600">
                {profile.name ? profile.name[0]?.toUpperCase() : "U"}
              </span>
            )}
            {editMode && (
              <span
                className={cn(
                  "pointer-events-none absolute inset-0 flex items-center justify-center transition-all duration-300 text-white",
                  profile.photoURL
                    ? "opacity-0 group-hover:opacity-90 bg-black/40 rounded-3xl"
                    : "opacity-30 group-hover:opacity-80 bg-black/20 rounded-3xl"
                )}
              >
                <CameraIcon className="w-12 h-12 text-white" />
              </span>
            )}
            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={!editMode}
              className="hidden"
            />
          </label>
          <div className="text-center">
            <h2 className="text-3xl font-bold leading-none text-slate-900 mb-2">
              {profile.name}
            </h2>
            <p className="text-xl font-bold text-emerald-600 mb-2">
              {profile.role &&
                profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
            </p>
            <p className="text-lg text-slate-500 font-medium">
              {profile.email}
            </p>
          </div>
        </div>

        <form
          className="space-y-8"
          onSubmit={(e) => {
            e.preventDefault();
            if (editMode) handleSave();
          }}
        >
          <div>
            <label className="block mb-3 text-lg font-bold text-slate-700">
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className={cn("input-field w-full", !editMode && "bg-slate-100")}
              value={profile.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              disabled={!editMode}
              required
              aria-label="Full name"
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <label className="block mb-3 text-lg font-bold text-slate-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              className="w-full bg-slate-100 input-field"
              value={profile.email}
              disabled
              aria-label="Email address"
            />
          </div>
          <div>
            <label className="block mb-3 text-lg font-bold text-slate-700">
              Location
            </label>
            <input
              id="location"
              name="location"
              type="text"
              className={cn("input-field w-full", !editMode && "bg-slate-100")}
              value={profile.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              disabled={!editMode}
              aria-label="Location"
              placeholder="Enter your location"
            />
          </div>
          <div>
            <label className="block mb-3 text-lg font-bold text-slate-700">
              Phone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              className={cn("input-field w-full", !editMode && "bg-slate-100")}
              value={profile.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              disabled={!editMode}
              aria-label="Phone number"
              placeholder="Enter your phone number"
            />
          </div>
          <div>
            <label className="block mb-3 text-lg font-bold text-slate-700">
              Role
            </label>
            <input
              id="role"
              name="role"
              type="text"
              className="w-full bg-slate-100 input-field"
              value={profile.role}
              disabled
              aria-label="User role"
            />
          </div>
          {error && (
            <div className="text-lg text-center text-red-500 font-bold">
              {error}
            </div>
          )}
          {success && (
            <div className="text-lg text-center text-emerald-600 font-bold">
              {success}
            </div>
          )}
          <div className="flex justify-center space-x-4">
            {editMode ? (
              <>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => {
                    setEditMode(false);
                    setError(null);
                    setSuccess(null);
                    if (newPhotoFile) URL.revokeObjectURL(profile.photoURL);
                    setNewPhotoFile(null);
                  }}
                  disabled={saveLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saveLoading}
                >
                  {saveLoading ? "Saving..." : "Save"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setEditMode(true)}
              >
                Edit Profile
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
